import React from "react"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router"

import { NzDataGrid } from "@/components/nzDataGrid"
import { PageCard } from "@/components/page-card"
import { PageTitle } from "@/components/page-title"
import { useActiveOrganization, useAuthentication } from "@/contexts"
import {
  createAdminGridDeleteAction,
  createAdminGridEditAction,
  createAdminGridViewAction,
  useAdminGridActionsColumn,
} from "@/modules/admin/components/admin-grid-actions-column"
import {
  CredentialWebsiteLink,
  useConfirmDeleteCredential,
} from "@/modules/admin/credentials"
import { DataGridFill, DataGridToolbar, baseColumnDef } from "@/nzds/data-grid"
import type { CredentialMeta } from "@/services/credential"
import { useCredentials } from "@/services/credential"
import { useUrl } from "@/utils/url"
import { getDisplayListValue, getDisplayValue } from "@/utils/value"

import { Button, Link as MuiLink, Stack } from "@mui/material"
import type { GridColDef, GridSlotProps, GridSlots } from "@mui/x-data-grid-pro"

interface CredentialGridRecord {
  id: string
  providerName: string
  resources: string
  website: string
}

const useCredentialsGridColumns = (
  getCredentialPath: (credentialId: string) => string
): GridColDef<CredentialGridRecord>[] => {
  const { t } = useTranslation("common")

  return [
    {
      ...baseColumnDef,
      field: "providerName",
      flex: 1,
      headerName: t("fields.utilityCredential.title"),
      minWidth: 220,
      renderCell: ({ row }) => (
        <MuiLink
          component={Link}
          to={getCredentialPath(row.id)}
          underline="hover"
        >
          {row.providerName}
        </MuiLink>
      ),
    },
    {
      ...baseColumnDef,
      field: "website",
      flex: 1,
      headerName: t("admin.credentialsGrid.website"),
      minWidth: 220,
      renderCell: ({ row }) => <CredentialWebsiteLink website={row.website} />,
    },
    {
      ...baseColumnDef,
      field: "resources",
      flex: 1,
      headerName: t("admin.credentialsGrid.resources"),
      minWidth: 220,
    },
  ]
}

const credentialToGridRecord = (
  credential: CredentialMeta
): CredentialGridRecord => ({
  id: credential.id,
  providerName: getDisplayValue(credential.providerName),
  resources: getDisplayListValue(credential.resources),
  website: credential.website ?? "",
})

export const AdminCredentialsRoute: FC = () => {
  const { t } = useTranslation("common")
  const { activeOrganization } = useActiveOrganization()
  const { canManageCredentials } = useAuthentication()
  const {
    adminCredentialEditPath,
    adminCredentialPath,
    adminCredentialsNewPath,
  } = useUrl()
  const activeOrganizationId = activeOrganization?.id

  const {
    credentialsData,
    credentialsIsFetching,
    credentialsIsPlaceholderData,
  } = useCredentials({
    organizationId: activeOrganizationId,
  })

  const rows: CredentialGridRecord[] = React.useMemo(() => {
    if (!credentialsData || credentialsIsPlaceholderData) {
      return []
    }

    return credentialsData.map(credentialToGridRecord)
  }, [credentialsData, credentialsIsPlaceholderData])

  const { confirmAndDeleteCredential, deleteCredentialIsPending } =
    useConfirmDeleteCredential({
      organizationId: activeOrganizationId ?? "",
    })

  const handleDeleteClick = (credential: CredentialGridRecord) => {
    confirmAndDeleteCredential({
      id: credential.id,
      providerName: credential.providerName,
    }).catch(() => undefined)
  }

  const baseColumns = useCredentialsGridColumns(adminCredentialPath)
  const actionsColumn = useAdminGridActionsColumn<CredentialGridRecord>({
    getActions: ({ row }) => {
      const credentialId = row.id
      const actions = [
        createAdminGridViewAction(
          adminCredentialPath(credentialId),
          t("button.view")
        ),
      ]

      if (canManageCredentials) {
        actions.push(
          createAdminGridEditAction(
            adminCredentialEditPath(credentialId),
            t("button.edit")
          ),
          createAdminGridDeleteAction({
            disabled: deleteCredentialIsPending,
            label: t("button.delete"),
            onDeleteClick: () => {
              handleDeleteClick(row)
            },
          })
        )
      }

      return actions
    },
    minWidth: canManageCredentials ? 160 : 100,
    width: canManageCredentials ? 160 : 100,
  })
  const columns = [...baseColumns, actionsColumn]
  if (!activeOrganizationId) {
    return null
  }

  return (
    <Stack sx={{ gap: 2, height: "100%", minHeight: 0 }}>
      <PageTitle
        heading={t("admin.credentials")}
        primaryActions={
          canManageCredentials ? (
            <Button
              component={Link}
              to={adminCredentialsNewPath}
              variant="contained"
            >
              {t("admin.credentialsUi.addCredentialButton")}
            </Button>
          ) : undefined
        }
      />
      <PageCard
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <DataGridFill>
          <NzDataGrid
            aria-label={t("admin.credentialsGrid.ariaLabel")}
            columns={columns}
            getRowId={(row: CredentialGridRecord) => row.id}
            initialState={{
              sorting: {
                sortModel: [
                  { field: "providerName", sort: "asc" },
                  { field: "website", sort: "asc" },
                ],
              },
            }}
            loading={credentialsIsFetching}
            rows={rows}
            slotProps={{
              toolbar: {
                isRecordCountLoading:
                  credentialsIsFetching || credentialsIsPlaceholderData,
                recordCount: credentialsData?.length,
              } as GridSlotProps["toolbar"],
            }}
            slots={{
              toolbar: DataGridToolbar as GridSlots["toolbar"],
            }}
            sortingMode="client"
          />
        </DataGridFill>
      </PageCard>
    </Stack>
  )
}
